import styled, { css, keyframes } from 'styled-components';

const enterFrom = (offset: string) => keyframes`
  from {
    opacity: 0;
    transform: translateX(${offset});
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

/**
 * The slide on screen. A plain CSS animation keyed per slide rather than a
 * framer-motion exit/enter pair: framer's `mode="wait"` never mounts the next
 * slide while a tab is not painting, and a blank modal is worse than no
 * animation.
 *
 * `pan-y` leaves vertical scrolling to the browser and hands the horizontal
 * drag to us, which is what makes it swipe on a phone.
 */
export const Slide = styled.div<{ $from: 'left' | 'right' | null }>`
  touch-action: pan-y;
  user-select: none;

  ${({ $from }) =>
    $from &&
    css`
      animation: ${enterFrom($from === 'right' ? '40px' : '-40px')} 220ms ease
        both;
    `}

  img {
    -webkit-user-drag: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Solid white behind the dots and the button, with a short fade above. The
 * modal footer's own gradient is transparent at the top, which is fine for a
 * lone button but left the dots sitting on top of the body text.
 */
export const Controls = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background: ${({ theme }) => theme.white};

  ::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: 24px;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.white}00,
      ${({ theme }) => theme.white}
    );
  }
`;

export const Dots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/** One dot per slide; the current one stretches into a short red pill. */
export const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '20px' : '8px')};
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ theme, $active }) =>
    $active ? theme.primary : `${theme.darkGray}${theme.transparency.t20}`};
  transition: ${({ theme }) => theme.transitions.ease200ms};
`;
